import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import PartnerLifecycleWizard from './PartnerLifecycleWizard';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [globalSearch, setGlobalSearch] = useState('');
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const avatarBtnRef = useRef(null);
  const dropdownRef = useRef(null);
  const globalSearchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const { unreadCount, notifications, markRead, markAllRead } = useNotifications();

  const navigationPhases = [
    {
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      items: [
        { path: '/executive-dashboard', label: 'Executive Dashboard', icon: 'LayoutDashboard', description: 'KPIs, metrics & performance overview' },
      ]
    },
    {
      label: 'Partner Setup',
      icon: 'Users',
      items: [
        { path: '/all-partners', label: 'All Partners', icon: 'Users', description: 'Browse and manage all partners' },
        { path: '/partner-registration-dashboard', label: 'Registration Dashboard', icon: 'UserPlus', description: 'Onboard and track new partners' },
        { path: '/portfolio-mapping-interface', label: 'Portfolio Mapping', icon: 'Map', description: 'Map products & services to partners' },
        { path: '/partner-profile-portal', label: 'Profile Portal', icon: 'User', description: 'Detailed partner profile management' }
      ]
    },
    {
      label: 'Catalogs',
      icon: 'Package',
      items: [
        { path: '/products-catalog', label: 'Products Catalog', icon: 'Package', description: 'Manage product listings' },
        { path: '/services-catalog', label: 'Services Catalog', icon: 'Wrench', description: 'Manage service offerings' },
        { path: '/bundles-catalog', label: 'Bundles Catalog', icon: 'Layers', description: 'Create and manage bundles' },
        { path: '/catalog-data-quality', label: 'Data Quality', icon: 'ShieldCheck', description: 'Monitor catalog data integrity' },
      ]
    },
    {
      label: 'Operations',
      icon: 'Briefcase',
      items: [
        { path: '/engagement-management-hub', label: 'Engagement Hub', icon: 'Users', description: 'Manage deals, tenders & projects' },
        { path: '/engagement-records', label: 'Engagement Records', icon: 'Database', description: 'Historical engagement data' }
      ]
    },
    {
      label: 'Strategy',
      icon: 'TrendingUp',
      items: [
        { path: '/partner-enablement-center', label: 'Enablement Center', icon: 'GraduationCap', description: 'Training, certifications & learning' },
        { path: '/strategic-growth-planning', label: 'Growth Planning', icon: 'TrendingUp', description: 'Revenue targets & joint marketing' }
      ]
    },
    {
      label: 'Reports',
      icon: 'BarChart3',
      items: [
        { path: '/reporting-export', label: 'Reporting & Export', icon: 'BarChart3', description: 'Generate and export reports' },
        { path: '/audit-trail', label: 'Audit Trail', icon: 'ClipboardList', description: 'System activity & change logs' }
      ]
    }
  ];

  // Flat list for global search
  const allNavItems = navigationPhases?.flatMap(p => p?.items);
  const filteredSearchItems = globalSearch?.trim()
    ? allNavItems?.filter(item =>
        item?.label?.toLowerCase()?.includes(globalSearch?.toLowerCase()) ||
        item?.description?.toLowerCase()?.includes(globalSearch?.toLowerCase())
      )
    : [];

  const isActivePhase = (phase) => {
    return phase?.items?.some(item => location?.pathname === item?.path);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    setShowUserMenu(false);
    await signOut();
    navigate('/login');
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      partner_manager: 'Partner Manager',
      finance: 'Finance',
      viewer: 'Viewer'
    };
    return labels?.[role] || role || 'User';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      partner_manager: 'bg-blue-100 text-blue-700',
      finance: 'bg-green-100 text-green-700',
      viewer: 'bg-slate-100 text-slate-600'
    };
    return colors?.[role] || 'bg-slate-100 text-slate-600';
  };

  const handleAvatarClick = () => {
    if (!showUserMenu && avatarBtnRef?.current) {
      const rect = avatarBtnRef?.current?.getBoundingClientRect();
      const rightFromEdge = window.innerWidth - rect?.right;
      const clampedRight = Math.max(8, rightFromEdge);
      setDropdownPos({
        top: rect?.bottom + 8,
        right: clampedRight,
      });
    }
    setShowUserMenu(!showUserMenu);
    setShowSearch(false);
    setShowGlobalSearch(false);
  };

  // Keyboard shortcut: Cmd/Ctrl+K to open global search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.metaKey || e?.ctrlKey) && e?.key === 'k') {
        e?.preventDefault();
        setShowGlobalSearch(true);
        setTimeout(() => globalSearchRef?.current?.focus(), 50);
      }
      if (e?.key === 'Escape') {
        setShowGlobalSearch(false);
        setGlobalSearch('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef?.current && !dropdownRef?.current?.contains(e?.target) &&
        avatarBtnRef?.current && !avatarBtnRef?.current?.contains(e?.target)
      ) {
        setShowUserMenu(false);
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const recentNotifications = notifications?.slice(0, 3) || [];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[1000] bg-white shadow-sm border-b border-slate-100">
        {/* Row 1 — Logo + Search + User Profile */}
        <div className="bg-white">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between h-[72px] px-6 pr-4 gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-xl blur-sm opacity-30 group-hover:opacity-50 transition-all duration-300"></div>
                <div className="relative flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl shadow-md">
                  <Icon name="Handshake" size={22} className="text-white" />
                </div>
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent font-heading leading-tight block">
                  PartnerHub
                </span>
                <span className="text-[10px] text-slate-400 font-medium tracking-widest uppercase">Partner Relationship Management</span>
              </div>
            </Link>

            {/* Global Search Bar */}
            <div className="hidden md:flex flex-1 max-w-md relative">
              <div
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-all duration-200 cursor-text ${
                  showGlobalSearch
                    ? 'border-primary/40 bg-white shadow-md ring-2 ring-primary/10'
                    : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
                }`}
                onClick={() => {
                  setShowGlobalSearch(true);
                  setTimeout(() => globalSearchRef?.current?.focus(), 50);
                }}
              >
                <Icon name="Search" size={15} className="text-slate-400 flex-none" />
                <input
                  ref={globalSearchRef}
                  type="text"
                  value={globalSearch}
                  onChange={e => setGlobalSearch(e?.target?.value)}
                  onFocus={() => setShowGlobalSearch(true)}
                  onBlur={() => setTimeout(() => { setShowGlobalSearch(false); setGlobalSearch(''); }, 200)}
                  placeholder="Search pages, features..."
                  className="flex-1 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
                />
                <div className="hidden lg:flex items-center gap-1 flex-none">
                  <kbd className="text-[10px] text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded font-mono">⌘K</kbd>
                </div>
              </div>

              {/* Search Results Dropdown */}
              {showGlobalSearch && filteredSearchItems?.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200/80 py-2 z-[9999] overflow-hidden">
                  <div className="px-3 pb-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pages</span>
                  </div>
                  {filteredSearchItems?.map(item => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      onMouseDown={e => e?.preventDefault()}
                      onClick={() => { setShowGlobalSearch(false); setGlobalSearch(''); }}
                      className="flex items-center gap-3 px-3 py-2.5 mx-2 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg flex items-center justify-center flex-none">
                        <Icon name={item?.icon} size={15} className="text-primary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-800">{item?.label}</div>
                        <div className="text-xs text-slate-400">{item?.description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Right — User Profile */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Mobile search icon */}
              <button
                className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Icon name="Search" size={20} />
              </button>

              {user && (
                <button
                  ref={avatarBtnRef}
                  onClick={handleAvatarClick}
                  className="relative flex items-center gap-2.5 px-3 py-2 rounded-xl border border-slate-200 hover:border-primary/30 hover:bg-primary/5 transition-all duration-200 shadow-sm group"
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-9 h-9 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm">
                      {(userProfile?.full_name || user?.email || 'U')?.charAt(0)?.toUpperCase()}
                    </div>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 leading-none ring-2 ring-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-semibold text-slate-900 leading-tight whitespace-nowrap">
                      {userProfile?.full_name || user?.email?.split('@')?.[0]}
                    </div>
                    <div className={`text-xs px-1.5 py-0.5 rounded-full font-medium mt-0.5 inline-block ${getRoleBadgeColor(userProfile?.role)}`}>
                      {getRoleLabel(userProfile?.role)}
                    </div>
                  </div>
                  <Icon name="ChevronDown" size={14} className={`text-slate-400 hidden md:block transition-transform duration-200 flex-shrink-0 ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>
              )}

              {/* Mobile menu toggle */}
              <button
                onClick={toggleMobileMenu}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-700"
                aria-label="Toggle mobile menu"
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={22} />
              </button>
            </div>
          </div>
        </div>

        {/* Row 2 — Navigation Bar */}
        <div className="bg-white">
          <div className="max-w-[1600px] mx-auto px-6">
            <nav className="hidden lg:flex items-center justify-center gap-1 h-[68px] relative">
              <div className="flex items-center gap-1.5 mx-auto">
              {navigationPhases?.map((phase) => (
                <div key={phase?.label} className="relative group">
                  <button
                    className={`
                      flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-all duration-200 whitespace-nowrap rounded-xl relative
                      ${isActivePhase(phase)
                        ? 'text-slate-900 bg-slate-100' :'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                      }
                    `}
                  >
                    <Icon name={phase?.icon} size={16} className={isActivePhase(phase) ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'} />
                    {phase?.label}
                    <Icon name="ChevronDown" size={13} className={`transition-transform duration-200 group-hover:rotate-180 ${isActivePhase(phase) ? 'text-blue-600' : 'text-slate-400'}`} />
                    {isActivePhase(phase) && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full" />
                    )}
                  </button>

                  {/* Dropdown */}
                  <div className="absolute top-full left-0 mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none group-hover:pointer-events-auto z-[9999] translate-y-1 group-hover:translate-y-0">
                    {/* Invisible bridge to fill the gap between button and dropdown */}
                    <div className="absolute -top-2 left-0 right-0 h-2" />
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 min-w-[300px] py-3 overflow-hidden">
                      {/* Dropdown header */}
                      <div className="px-4 pb-2 mb-1 border-b border-slate-100 flex items-center gap-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                          <Icon name={phase?.icon} size={12} className="text-white" />
                        </div>
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{phase?.label}</span>
                      </div>
                      {phase?.items?.map((item) => (
                        <Link
                          key={item?.path}
                          to={item?.path}
                          className={`
                            flex items-center gap-3 px-4 py-2.5 mx-2 my-0.5 rounded-xl
                            text-sm transition-all duration-150
                            ${isActivePath(item?.path)
                              ? 'bg-gradient-to-r from-primary/10 to-secondary/10 text-primary' :'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                            }
                          `}
                        >
                          <div className={`p-1.5 rounded-lg flex-none ${
                            isActivePath(item?.path)
                              ? 'bg-gradient-to-br from-primary to-secondary text-white shadow-sm'
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            <Icon name={item?.icon} size={14} />
                          </div>
                          <div className="min-w-0">
                            <div className={`font-medium leading-tight ${isActivePath(item?.path) ? 'text-primary' : 'text-slate-800'}`}>
                              {item?.label}
                            </div>
                            {item?.description && (
                              <div className="text-xs text-slate-400 mt-0.5 truncate">{item?.description}</div>
                            )}
                          </div>
                          {isActivePath(item?.path) && (
                            <Icon name="Check" size={14} className="text-primary ml-auto flex-none" />
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              </div>

              {/* Right side — quick stats */}
              <div className="absolute right-1 flex items-center gap-2 pr-1">
                {/* Partner Journey Button */}
                <button
                  onClick={() => setShowWizard(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-xs font-bold shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
                >
                  <Icon name="Zap" size={13} className="text-yellow-300" />
                  Start Partner Journey
                </button>
                {unreadCount > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/30">
                    <Icon name="Bell" size={13} className="text-red-400" />
                    <span className="text-xs font-semibold text-red-300">{unreadCount} new</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                  <span className="text-xs text-slate-400 font-medium">Live</span>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Partner Lifecycle Wizard */}
      <PartnerLifecycleWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onComplete={() => {
          // Dispatch a custom event so All Partners page can reload
          window.dispatchEvent(new CustomEvent('wizard:partner-created'));
        }}
      />

      {/* Fixed Profile Dropdown — rendered outside header to avoid clipping */}
      {showUserMenu && user && (
        <div
          ref={dropdownRef}
          style={{ top: dropdownPos?.top, right: dropdownPos?.right }}
          className="fixed w-80 bg-white rounded-2xl shadow-2xl border border-slate-200/80 py-2 z-[9999] overflow-hidden"
        >
          {/* Search row */}
          <div className="px-3 py-1">
            {showSearch ? (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 rounded-xl border border-slate-200">
                <Icon name="Search" size={15} className="text-slate-400 flex-none" />
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  placeholder="Search..."
                  className="flex-1 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
                />
                <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="text-slate-400 hover:text-slate-600">
                  <Icon name="X" size={13} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowSearch(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-700 hover:bg-slate-50 transition-smooth"
              >
                <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center flex-none">
                  <Icon name="Search" size={15} className="text-slate-600" />
                </div>
                <span className="font-medium">Search</span>
              </button>
            )}
          </div>

          {/* Notifications row */}
          <div className="px-3 py-1">
            <div className="px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-smooth">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="relative w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center flex-none">
                    <Icon name="Bell" size={15} className="text-slate-600" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-[8px] font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium text-slate-700">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="text-xs bg-red-100 text-red-600 font-semibold px-1.5 py-0.5 rounded-full">{unreadCount} new</span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-primary hover:underline font-medium">
                    Mark all read
                  </button>
                )}
              </div>
              {recentNotifications?.length > 0 ? (
                <div className="space-y-1 ml-11">
                  {recentNotifications?.map(n => (
                    <div
                      key={n?.id}
                      onClick={() => markRead(n?.id)}
                      className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-smooth ${!n?.is_read ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-slate-50'}`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-none ${!n?.is_read ? 'bg-primary' : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-slate-800 truncate">{n?.title}</div>
                        <div className="text-xs text-slate-500 truncate">{n?.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ml-11 text-xs text-slate-400 py-1">No notifications</div>
              )}
            </div>
          </div>

          {/* Settings row */}
          <div className="px-3 py-1">
            <button
              onClick={() => setShowUserMenu(false)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-700 hover:bg-slate-50 transition-smooth"
            >
              <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center flex-none">
                <Icon name="Settings" size={15} className="text-slate-600" />
              </div>
              <span className="font-medium">Settings</span>
            </button>
          </div>

          {/* Divider */}
          <div className="mx-4 my-2 border-t border-slate-100" />

          {/* User info */}
          <div className="px-5 py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-sm font-bold flex-none shadow-sm">
                {(userProfile?.full_name || user?.email || 'U')?.charAt(0)?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{userProfile?.full_name || user?.email?.split('@')?.[0]}</div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="mx-4 my-2 border-t border-slate-100" />

          {/* Sign Out */}
          <div className="px-3 py-1 pb-2">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-smooth"
            >
              <div className="w-8 h-8 bg-red-50 rounded-xl flex items-center justify-center flex-none">
                <Icon name="LogOut" size={15} className="text-red-500" />
              </div>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed top-[120px] left-0 right-0 z-[999] bg-white/95 backdrop-blur-xl border-t border-slate-200/60 shadow-lg">
          <nav className="px-6 py-4 space-y-3 max-h-[calc(100vh-92px)] overflow-y-auto">
            {navigationPhases?.map((phase) => (
              <div key={phase?.label} className="space-y-2">
                <div className="flex items-center gap-2 px-3 py-2">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
                  <div className="flex items-center gap-1.5">
                    <Icon name={phase?.icon} size={12} className="text-slate-400" />
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">{phase?.label}</div>
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
                </div>
                {phase?.items?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-xl
                      text-sm font-medium transition-smooth
                      ${isActivePath(item?.path)
                        ? 'bg-gradient-to-r from-primary/10 to-secondary/10 text-primary border border-primary/20' :'text-slate-700 hover:bg-slate-50 active:bg-slate-100'
                      }
                    `}
                  >
                    <div className={`p-2 rounded-lg ${
                      isActivePath(item?.path)
                        ? 'bg-gradient-to-br from-primary to-secondary text-white' :'bg-slate-100 text-slate-600'
                    }`}>
                      <Icon name={item?.icon} size={17} />
                    </div>
                    <div>
                      <div>{item?.label}</div>
                      {item?.description && <div className="text-xs text-slate-400 font-normal mt-0.5">{item?.description}</div>}
                    </div>
                  </Link>
                ))}
              </div>
            ))}
            {user && (
              <div className="pt-4 border-t border-slate-200">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-red-600 hover:bg-red-50 transition-smooth"
                >
                  <Icon name="LogOut" size={17} />
                  Sign Out
                </button>
              </div>
            )}
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;